import FW from '../fw/fw';
import Logger from '../fw/logger';
import HlsJS from '../streaming/hls-js/hls-js';
import ShakaPlayer from '../streaming/shaka-player/shaka-player';


export default class PlaybackManagement {

  #rmp;
  #playerInitialized = false;
  #adImaDisplayContainerInitialized = false;
  #firstPlay = true;
  #firstFrameReached = false;
  #startUpPlay = 0;
  #startUpTime = -1;
  #startUpLogPlayingFn = null;
  #onAdAutoplayFailedFn = null;
  #onAdAutoplaySuccessFn = null;
  #processInitiatePlaybackFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get startUpTime() {
    return this.#startUpTime;
  }

  get firstPlay() {
    return this.#firstPlay;
  }

  get playerInitialized() {
    return this.#playerInitialized;
  }

  set playerInitialized(initialized) {
    this.#playerInitialized = initialized;
  }

  get adImaDisplayContainerInitialized() {
    return this.#adImaDisplayContainerInitialized;
  }

  get firstFrameReached() {
    return this.#firstFrameReached;
  }

  #startUpLogPlaying() {
    this.#rmp.off('playing adloaded', this.#startUpLogPlayingFn);
    window.setTimeout(() => {
      const startUpPlayingTime = FW.getNow();
      if (this.#startUpPlay > 0 && (startUpPlayingTime > this.#startUpPlay)) {
        this.#startUpTime = startUpPlayingTime - this.#startUpPlay;
      }
      Logger.print('info', 'computed startup time is ' + this.#startUpTime + ' ms');
      this.#firstFrameReached = true;
      this.#rmp.dispatch('firstframe');
    }, 1);
  }

  #onAdAutoplayFailed() {
    this.#rmp.rmpUtils.autoplayFailureRestorePlayUI();
  }

  #onAdAutoplaySuccess() {
    const adIsLinear = this.#rmp.adLinear;
    if (adIsLinear !== null && (adIsLinear || (!adIsLinear && this.#rmp.adParser === 'rmp-vast'))) {
      this.#rmp.rmpUtils.fireAutoplaySuccess();
    }
  }

  #firstPlaySteps() {
    Logger.print('info', 'processing first play request now');
    // startup time
    this.#startUpLogPlayingFn = this.#startUpLogPlaying.bind(this);
    this.#rmp.on('playing adloaded', this.#startUpLogPlayingFn);
    if (this.#rmp.ads && !this.#rmp.adImaDai) {
      if (this.#rmp.autoplay) {
        this.#onAdAutoplaySuccessFn = this.#onAdAutoplaySuccess.bind(this);
        this.#rmp.on('adstarted adinitialplayrequestsucceeded', this.#onAdAutoplaySuccessFn);
        this.#onAdAutoplayFailedFn = this.#onAdAutoplayFailed.bind(this);
        this.#rmp.on('adinitialplayrequestfailed', this.#onAdAutoplayFailedFn);
      }
      if (this.#rmp.emptyInitAdTagUrl) {
        Logger.print('advertisement', 'no initial ad tag URL - do not request ads now');
        this.#rmp.rmpUtils.playPromise();
        return;
      }
      Logger.print('advertisement', 'initial ad request');
      if (this.#rmp.rmpAdScheduler) {
        this.#rmp.adTagUrl = this.#rmp.adSchedule.preroll;
        this.#rmp.rmpAdScheduler.processAdScheduleCallback(this.#rmp.adTagUrl, true);
      } else {
        this.#rmp.loadAds(this.#rmp.adTagUrl, ['internal'], true).catch(error => {
          this.#rmp.rmpUtils.warning(1020, error);
          this.#rmp.rmpUtils.playPromise();
        });
      }
      this.#rmp.resolvePromise('play');
    } else {
      this.#rmp.rmpUtils.playPromise();
    }
  }

  #doPlay() {
    // googleCast
    if (this.#rmp.rmpGoogleCast) {
      if (this.#rmp.googleCastCordova && this.#rmp.rmpGoogleCast.isCastMediaLoaded()) {
        if (this.#rmp.rmpGoogleCast.castMediaEnded) {
          this.#rmp.rmpGoogleCast.loadCastMedia();
          this.#rmp.resolvePromise('play');
          return;
        }
        if (this.#rmp.castMedia) {
          if (this.#rmp.paused) {
            this.#rmp.castMedia.play({});
            this.#rmp.rmpEventsManagement.playCallback();
            this.#rmp.resolvePromise('play');
          } else {
            this.#rmp.castMedia.pause({});
            this.#rmp.rmpEventsManagement.pauseCallback();
          }
        }
        return;
      } else {
        // if casting engaged play/pause remote player
        if (this.#rmp.rmpGoogleCast.isCastMediaLoaded()) {
          if (this.#rmp.remotePlayer.isPaused) {
            this.#rmp.rmpGoogleCast.remotePlayerController.playOrPause();
            this.#rmp.resolvePromise('play');
          } else {
            this.#rmp.rmpGoogleCast.remotePlayerController.playOrPause();
          }
          return;
        }
        // if media is loading on cast device - ignore play request
        if (this.#rmp.googleCastConnected && this.#rmp.rmpGoogleCast.remotePlayerState === 'LOADING') {
          this.#rmp.rmpUtils.warning(16004);
          this.#rmp.rejectPromise('play', this.#rmp.warningData);
          return;
        }
      }
    }
    if (this.#firstPlay) {
      this.#firstPlay = false;
      this.#firstPlaySteps();
      return;
    }
    if (this.#rmp.dom.video.paused) {
      this.#rmp.rmpUtils.playPromise();
    } else {
      this.#rmp.dom.video.pause();
    }
  }

  #processInitiatePlayback() {
    if (this.#processInitiatePlaybackFn !== null) {
      this.#rmp.off('loadedmetadata', this.#processInitiatePlaybackFn);
    }
    // At first play - 360 video player can be ready and loadedmetadata available 
    // but three.js may still be loading/delayed we need to check for THREE
    if (this.#rmp.video360 && this.#firstPlay && typeof window.THREE === 'undefined') {
      window.setTimeout(() => {
        this.#processInitiatePlayback();
      }, 40);
      return;
    }
    if (this.#rmp.ads && this.#firstPlay && this.#rmp.adParserLoaded) {
      this.#doPlay();
    } else {
      if (this.#rmp.linearAdOnStage && !this.#rmp.adImaDai) {
        if (this.#rmp.googleCastConnected) {
          this.#doPlay();
        } else if (this.#rmp.rmpAdsIma) {
          if (this.#rmp.adPaused) {
            this.#rmp.rmpAdsIma.resumeImaAds();
            this.#rmp.resolvePromise('play');
          } else {
            this.#rmp.rmpAdsIma.pauseImaAds();
          }
        } else if (this.#rmp.rmpAdsRmpVast) {
          if (this.#rmp.adPaused) {
            this.#rmp.rmpAdsRmpVast.resumeRmpVastAds();
            this.#rmp.resolvePromise('play');
          } else {
            this.#rmp.rmpAdsRmpVast.pauseRmpVastAds();
          }
        }
      } else if (!this.#rmp.adOutStream) {
        this.#doPlay();
      }
    }
  }

  async #resumePlaybackInit() {
    if (!this.#playerInitialized) {
      Logger.print('info', 'starting player initialization');
      this.#rmp.rmpUIManagement.showLoadingSpin();
      let requiresLoad = false;
      if (!this.#rmp.readingMse && (!this.#rmp.hasLoadedMetadata || this.#rmp.preload === 'none')) {
        requiresLoad = true;
      }
      Logger.print('info', 'requiresLoad ' + requiresLoad);
      // initialize player - must be done in same callback as click/touchend 
      // e.g. not in a different function or deferred 
      this.#playerInitialized = true;
      this.#rmp.hidePoster();
      if (this.#rmp.ads && !this.#rmp.adImaDai) {
        // here either adParserLoaded = true and we continue
        // or adParserLoadError = true and then ads = false which means we do not run here
        // does not apply to DAI
        if (this.#rmp.rmpAdsIma) {
          try {
            this.#rmp.rmpAdsIma.adDisplayContainer.initialize();
          } catch (warning) {
            console.warn(warning);
          }
          this.#adImaDisplayContainerInitialized = true;
          if (requiresLoad) {
            this.#rmp.dom.video.load();
          }
        } else if (this.#rmp.rmpVast) {
          this.#rmp.rmpVast.initialize();
          if (requiresLoad) {
            this.#rmp.dom.video.load();
          }
        }
      } else {
        // no ad 
        // we need init on mobile devices and macOS Safari 11+ (no MSE eg preload auto MPEG-DASH) ...
        // when forceHlsJSOnAppleDevices|forceMseHlsOnAppleDevices is set on MacosSafari we still need to load while with Shaka we do not (weird!)
        if (requiresLoad) {
          // even if preload !== 'none' we cannot guarentee that the loading process has started (preloading may be blocked by browser)
          this.#rmp.dom.video.load();
        }
      }
      if (this.#rmp.readingHlsJS) {
        if (!this.#rmp.rmpHlsJS) {
          this.#rmp.rmpHlsJS = new HlsJS(this.#rmp);
        }
        if (this.#rmp.hlsJSPreloadManager) {
          Logger.print('hls.js', 'resume content loading after preloading metadata');
          this.#rmp.hlsJS.startLoad();
          this.#rmp.rmpHlsJS.unwireFragFirstCheck();
          this.#rmp.hlsJSPreloadManager = false;
        }
        this.#rmp.rmpHlsJS.init();
      } else if (this.#rmp.readingShaka) {
        if (!this.#rmp.rmpShakaPlayer) {
          this.#rmp.rmpShakaPlayer = new ShakaPlayer(this.#rmp);
        }
        if (this.#rmp.shakaPreloadManager) {
          Logger.print('shaka-player', 'resume content loading after preloading metadata');
          try {
            await this.#rmp.shakaPlayer.load(this.#rmp.shakaPreloadManager);
            await this.#rmp.shakaPreloadManager.destroy();
          } catch (error) {
            this.#rmp.rmpShakaPlayer.shakaError(error);
          }
          this.#rmp.shakaPreloadManager = null;
        }
        this.#rmp.rmpShakaPlayer.init();
      }
    }
    if (this.#rmp.hasLoadedMetadata) {
      this.#processInitiatePlayback();
    } else if (this.#processInitiatePlaybackFn === null) {
      this.#processInitiatePlaybackFn = this.#processInitiatePlayback.bind(this);
      this.#rmp.on('loadedmetadata', this.#processInitiatePlaybackFn);
    }
  }

  unwireAdAutoplayEvents() {
    this.#rmp.off('adstarted adinitialplayrequestsucceeded', this.#onAdAutoplaySuccessFn);
    this.#rmp.off('adinitialplayrequestfailed', this.#onAdAutoplayFailedFn);
  }

  init(event) {
    FW.eventStopPrevent(event);
    if (!this.#rmp.playerReady) {
      this.#rmp.rmpUtils.warning(1012, event);
      this.#rmp.rejectPromise('play', this.#rmp.warningData);
      return;
    }
    if (this.#firstPlay) {
      if (this.#rmp.ads && !this.#rmp.adParserLoaded) {
        this.#rmp.rmpUtils.warning(10004, event);
        this.#rmp.rejectPromise('play', this.#rmp.warningData);
        return;
      }
      // init time for startup time
      this.#startUpPlay = FW.getNow();
    }
    if (this.#rmp.hasPlaylist && this.#rmp.playlistCallback !== null && this.#firstPlay) {
      if (this.#rmp.ads && this.#rmp.adTagUrl === '') {
        this.#rmp.emptyInitAdTagUrl = false;
      }
      this.#rmp.rmpPlaylist.playlistCallbackPromise().then(() => {
        this.#resumePlaybackInit();
      }).catch(warning => {
        console.warn(warning);
        this.#resumePlaybackInit();
      });
    } else if (this.#rmp.hasRelated && this.#rmp.relatedCallback !== null && this.#firstPlay) {
      if (this.#rmp.ads && this.#rmp.adTagUrl === '') {
        this.#rmp.emptyInitAdTagUrl = false;
      }
      this.#rmp.rmpRelated.relatedCallbackPromise().then(() => {
        this.#resumePlaybackInit();
      }).catch(warning => {
        console.warn(warning);
        this.#resumePlaybackInit();
      });
    } else {
      this.#resumePlaybackInit();
    }
  }

}
